import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from 'api/Api';
import { invalidateAuthTags } from 'api/AuthApi';
import { Contact, ContactRelationEnum } from 'api/ContactsApi';
import { DesignProps } from 'api/DesignApi';
import { User } from 'api/UsersApi';

export interface MessageTemplates {
  messageTemplates: MessageTemplate[];
  messageIdeas: MessageIdeas[];
}

export interface MessageTemplate {
  type: MessageTypeEnum;
  recipients: ContactRelationEnum[];
  eventType: MessageEventTypeEnum;
  title: string;
  content: string;
}

export interface MessageIdeas {
  type: MessageTypeEnum;
  title: string;
  content: string;
}

export interface Message {
  id: string;
  userId?: string | null;
  patientProfileId?: string | null;
  patientProfile?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null;
  type: MessageTypeEnum;
  status: MessageStatusEnum;
  name: string;
  content: string;
  contacts: Contact[];
  eventType: string;
  eventHint?: string;
  deliveryYear: number | null;
  deliveryMonth: number | null;
  deliveryDay: number | null;
  fileName: string;
  user?: User;
  aiTones?: string[];
  aiFocus: string;
  aiMemory: string;
  aiClosing: string;
  aiOtherInfo: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertMessage extends Omit<Message, 'contacts' | 'createdAt' | 'updatedAt'> {
  contactIds: string[];
}

const asStringOrNull = (value: unknown): string | null => (typeof value === 'string' && value ? value : null);

type RawMessage = Message & {
  profileId?: unknown;
};

export const normalizeMessage = (message: Message): Message => {
  const source = message as RawMessage;

  return {
    ...message,
    userId: asStringOrNull(source.userId),
    patientProfileId: asStringOrNull(source.patientProfileId) ?? asStringOrNull(source.profileId)
  };
};

export const dedupeMessagesById = (messages: Message[]): Message[] => {
  const seen = new Set<string>();

  return messages.filter((message) => {
    if (seen.has(message.id)) {
      return false;
    }

    seen.add(message.id);
    return true;
  });
};

export interface FileMessage {
  fileName?: string;
  signedUrl?: string;
}

export enum MessageTypeEnum {
  Text = 'text',
  Audio = 'audio',
  Video = 'video'
}

export enum MessageStatusEnum {
  Draft = 'draft',
  Published = 'published',
  Delivering = 'delivering',
  Delivered = 'delivered'
}

export enum MessageEventTypeEnum {
  Final = 'final',
  Birthday = 'birthday',
  Holiday = 'holiday',
  Anniversary = 'anniversary',
  Other = 'other',
  Future = 'future'
}

export type ErrorResponseType = {
  status: number;
  data: { success: boolean; message: string; errors: { stack: string } };
};

export const invalidateMessagesTags = () => messagesApi.util.invalidateTags(['Messages']);

export const messagesApi = createApi({
  baseQuery: baseQueryWithReAuth,
  reducerPath: 'messagesApi',
  tagTypes: ['Messages', 'MessageFiles', 'MessageTemplates'],
  endpoints: (build) => ({
    getMessageTemplates: build.query<MessageTemplates, void>({
      query: () => ({ url: '/messages/templates' }),
      transformResponse: (response: { data: MessageTemplates }) => response.data,
      providesTags: () => ['MessageTemplates']
    }),
    getAllMessages: build.query<
      { list: Message[]; total: number },
      { limit?: number; offset?: number; status?: MessageStatusEnum }
    >({
      query: (params) => ({ url: '/messages', params }),
      transformResponse: (response: { data: [Message[], number] }) => ({
        list: dedupeMessagesById(response.data[0].map((message) => normalizeMessage(message))),
        total: response.data[1]
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.list.map(({ id }) => ({
                type: 'Messages' as const,
                id
              })),
              { type: 'Messages', id: 'LIST' }
            ]
          : [{ type: 'Messages', id: 'LIST' }]
    }),
    getOneMessage: build.query<Message, Pick<Message, 'id'>>({
      query: ({ id }) => ({ url: `/messages/${id}` }),
      transformResponse: (response: { data: Message }) => normalizeMessage(response.data),
      providesTags: (result, error, { id }) => [{ type: 'Messages', id }]
    }),
    getOneMessagePublic: build.query<{ message: Message; designProps: DesignProps }, { messageContactId: string }>({
      query: ({ messageContactId }) => ({ url: `/public/messages/contacts/${messageContactId}` }),
      transformResponse: (response: { data: { message: Message; designProps: DesignProps } }) => ({
        ...response.data,
        message: normalizeMessage(response.data.message)
      }),
      providesTags: (result, error, { messageContactId }) => [{ type: 'Messages', messageContactId }]
    }),
    getFileMessage: build.query<FileMessage, Pick<Message, 'id'>>({
      query: ({ id }) => ({ url: `/messages/${id}/file` }),
      transformResponse: (response: { data: FileMessage }) => response.data,
      providesTags: (result, error, { id }) => [{ type: 'MessageFiles', id }]
    }),
    getFileMessagePublic: build.query<FileMessage, { messageContactId: string }>({
      query: ({ messageContactId }) => ({ url: `/public/messages/contacts/${messageContactId}/file` }),
      transformResponse: (response: { data: FileMessage }) => response.data,
      providesTags: (result, error, { messageContactId }) => [{ type: 'MessageFiles', messageContactId }]
    }),
    initMessage: build.mutation<Message, Pick<UpsertMessage, 'type' | 'contactIds' | 'patientProfileId'>>({
      query: (body) => ({
        url: `/messages/init`,
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: Message }) => normalizeMessage(response.data),
      invalidatesTags: (result) =>
        result?.id
          ? [
              { type: 'Messages', id: result.id },
              { type: 'Messages', id: 'LIST' },
              { type: 'MessageFiles', id: result.id },
              { type: 'MessageFiles', id: 'LIST' }
            ]
          : [
              { type: 'Messages', id: 'LIST' },
              { type: 'MessageFiles', id: 'LIST' }
            ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(invalidateAuthTags());
        } catch (e) {}
      }
    }),
    patchMessage: build.mutation<Message, Partial<UpsertMessage> & Pick<UpsertMessage, 'id'>>({
      query: ({ id, ...body }) => ({
        url: `/messages/${id}`,
        method: 'PATCH',
        body
      }),
      transformResponse: (response: { data: Message }) => normalizeMessage(response.data),
      invalidatesTags: (result, error, { id }) =>
        id
          ? [
              { type: 'Messages', id },
              { type: 'MessageFiles', id }
            ]
          : [
              { type: 'Messages', id: 'LIST' },
              { type: 'MessageFiles', id: 'LIST' }
            ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(invalidateAuthTags());
        } catch (e) {}
      }
    }),
    upsertMessage: build.mutation<Message, Partial<UpsertMessage>>({
      query: (body) => ({
        url: `/messages/upsert`,
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: Message }) => normalizeMessage(response.data),
      invalidatesTags: (result, error, { id }) =>
        id
          ? [
              { type: 'Messages', id },
              { type: 'MessageFiles', id }
            ]
          : [
              { type: 'Messages', id: 'LIST' },
              { type: 'MessageFiles', id: 'LIST' }
            ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(invalidateAuthTags());
        } catch (e) {}
      }
    }),
    publishMessage: build.mutation<Message, Pick<Message, 'id'> & Partial<Pick<Message, 'name'>>>({
      query: ({ id, ...body }) => ({
        url: `/messages/${id}/publish`,
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: Message }) => normalizeMessage(response.data),
      invalidatesTags: (result, error, { id }) => [id ? { type: 'Messages', id } : { type: 'Messages', id: 'LIST' }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(invalidateAuthTags());
        } catch (e) {}
      }
    }),
    unpublishMessage: build.mutation<Message, Pick<Message, 'id'>>({
      query: ({ id }) => ({
        url: `/messages/${id}/unpublish`,
        method: 'POST'
      }),
      transformResponse: (response: { data: Message }) => normalizeMessage(response.data),
      invalidatesTags: (result, error, { id }) => [id ? { type: 'Messages', id } : { type: 'Messages', id: 'LIST' }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(invalidateAuthTags());
        } catch (e) {}
      }
    }),
    getUploadSignedUrlMessage: build.mutation<
      string,
      Pick<Message, 'id'> & { fileName: string; contentType: string; fileSizeBytes: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/messages/${id}/upload-signed-url`,
        method: 'POST',
        body
      }),
      transformResponse: (response: { data: { uploadSignedUrl: string } }) => response.data.uploadSignedUrl
    }),
    removeMessage: build.mutation<boolean, Pick<Message, 'id'>>({
      query: ({ id }) => ({
        url: `/messages/${id}`,
        method: 'DELETE'
      }),
      transformResponse: (response: { data: { deleted: boolean } }) => response.data.deleted,
      invalidatesTags: () => [
        { type: 'Messages', id: 'LIST' },
        { type: 'MessageFiles', id: 'LIST' }
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(invalidateAuthTags());
        } catch (e) {}
      }
    })
  })
});

export const {
  useGetMessageTemplatesQuery,
  useGetAllMessagesQuery,
  useGetOneMessageQuery,
  useGetOneMessagePublicQuery,
  useGetFileMessagePublicQuery,
  useInitMessageMutation,
  usePatchMessageMutation,
  useUpsertMessageMutation,
  usePublishMessageMutation,
  useUnpublishMessageMutation,
  useGetUploadSignedUrlMessageMutation,
  useGetFileMessageQuery,
  useRemoveMessageMutation
} = messagesApi;
